import { createSlice, createSelector, nanoid } from "@reduxjs/toolkit";
import imageData from "../shared/imageData.json";
import colorData from "../shared/colorData.json";
import syncedStorage from "../services/syncedStorage";
import { isAlive, liveRows, stampRow, tombstoneFor } from "../utils/syncMeta";
import secureLogger from '../utils/secureLogger';
import subscriptionService from "../services/subscriptionService";
import userService from "../services/userService";
import { generateInvoiceNumber } from "../utils/invoiceNumberGenerator";
import {
  QUOTATIONS_KEY,
  DEFAULT_QUOTATION_COLOR,
  DEFAULT_QUOTATION_BG,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
} from "../constants/localKeys";

// Sum of all unpaid quotations
export const getTotalDueAmount = (state) => {
  try {
    const total = state.quotations.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Unpaid") {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total due amount:", error);
    return 0;
  }
};

// Sum of all paid quotations
export const getTotalCollectedAmount = (state) => {
  try {
    const total = state.quotations.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Paid") {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total collected amount:", error);
    return 0;
  }
};

// Helper function to check if a date is within range
const isDateInRange = (dateString, startDate, endDate) => {
  if (!dateString || !startDate || !endDate) return true;
  
  const date = new Date(dateString);
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set time to start/end of day for proper comparison
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  
  return date >= start && date <= end;
};

// Sum of unpaid quotations within date range (filtered by createdDate)
export const getTotalDueAmountByDateRange = (startDate, endDate) => (state) => {
  try {
    const total = state.quotations.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Unpaid" && isDateInRange(next.createdDate, startDate, endDate)) {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total due amount by date range:", error);
    return 0;
  }
};

// Sum of paid quotations within date range (filtered by createdDate)
export const getTotalCollectedAmountByDateRange = (startDate, endDate) => (state) => {
  try {
    const total = state.quotations.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Paid" && isDateInRange(next.createdDate, startDate, endDate)) {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total collected amount:", error);
    return 0;
  }
};

// Compute default dates: created now, due 30 days later
const __now = new Date();
const __dueIn30Days = new Date(__now.getTime() + 30 * 24 * 60 * 60 * 1000);

const initialState = {
  isConfirmModal: false,
  isConfirm: false,
  settingOpen: false,
  defaultColor: colorData[0],
  defaultBgImage: imageData[0],
  colors: colorData,
  images: imageData,
  data: [],
  detailList: [],
  deletedID: null,
  currentEditedID: null,
  newForm: {
    id: nanoid(),
    quotationNo: "",
    statusIndex: "1",
    statusName: "Draft",
    totalAmount: 1200,
    color: colorData[0],
    backgroundImage: imageData[0],
    dueDate: __dueIn30Days,
    createdDate: __now,
    currencyUnit: "₹",
    clientDetail: {
      id: "",
      name: "",
      mobileNo: "",
      email: "",
      image: "",
      billingAddress: "",
    },
    products: [
      {
        amount: 1200,
        id: "D9vPlvwg11cxYJToEf3x4",
        name: "productName",
        productID: "",
        quantity: 1,
      },
    ],
    taxes: [],
  },
};

export const quotationSlice = createSlice({
  name: "quotations",
  initialState,
  reducers: {
    setAllQuotation: (state, action) => {
      let quotationData = action.payload || [];
      
      // Apply subscription limits to existing data
      const user = userService.getCurrentUser();
      if (user) {
        const plans = subscriptionService.getSubscriptionPlans();
        const currentPlan = plans[user.subscription_tier] || plans.free;
        const quotationLimit = currentPlan.limitations.invoicesSaveExport; // Using same limit as invoices
        
        // If user has a limit and data exceeds it, truncate the data
        if (quotationLimit !== -1 && quotationData.length > quotationLimit) {
          console.log(`QUOTATION LIMIT ENFORCEMENT: Truncating ${quotationData.length} quotations to ${quotationLimit} for ${currentPlan.name} plan`);
          quotationData = quotationData.slice(0, quotationLimit);
        }
      }
      
      state.data = [...quotationData];
    },

    setAllQuotationDetailList: (state, action) => {
      let quotationDetailData = action.payload || [];
      
      // Apply subscription limits to existing data
      const user = userService.getCurrentUser();
      if (user) {
        const plans = subscriptionService.getSubscriptionPlans();
        const currentPlan = plans[user.subscription_tier] || plans.free;
        const quotationLimit = currentPlan.limitations.invoicesSaveExport; // Using same limit as invoices
        
        // If user has a limit and data exceeds it, truncate the data
        if (quotationLimit !== -1 && quotationDetailData.length > quotationLimit) {
          console.log(`QUOTATION DETAIL LIMIT ENFORCEMENT: Truncating ${quotationDetailData.length} quotation details to ${quotationLimit} for ${currentPlan.name} plan`);
          quotationDetailData = quotationDetailData.slice(0, quotationLimit);
        }
      }
      
      try {
        // Validate and clean quotation data to prevent corruption issues.
        // Tombstones (id + _deleted, no quotationNo) pass through untouched —
        // they must survive hydration so the next sync upload can propagate
        // the delete to other devices.
        const validatedQuotations = quotationDetailData.filter(quotation => {
          if (!quotation?.id) return false;
          if (quotation._deleted) return true; // tombstone — keep
          if (!quotation.quotationNo) {
            console.warn('Invalid quotation detected during load, skipping:', quotation?.id || 'unknown');
            return false;
          }
          return true;
        }).map(quotation => {
          if (quotation._deleted) return quotation; // don't enrich tombstones
          // Ensure all required fields have default values
          return {
            ...quotation,
            statusIndex: quotation.statusIndex || "1",
            statusName: quotation.statusName || "Draft",
            totalAmount: quotation.totalAmount || 0,
            dueDate: quotation.dueDate ? new Date(quotation.dueDate) : new Date(),
            createdDate: quotation.createdDate ? new Date(quotation.createdDate) : new Date(),
            clientDetail: quotation.clientDetail || { name: "", email: "", mobileNo: "", billingAddress: "" },
            companyDetail: quotation.companyDetail || {},
            products: Array.isArray(quotation.products) ? quotation.products : [],
            taxes: Array.isArray(quotation.taxes) ? quotation.taxes : []
          };
        });

        state.detailList = [...validatedQuotations];
        
      } catch (error) {
        console.error('Error validating quotation detail list:', error);
        state.detailList = [];
      }
    },

    setNewQuotations: (state, action) => {
      const user = userService.getCurrentUser();
      if (!user) {
        console.log('QUOTATION CREATION BLOCKED: No user found');
        return;
      }
      
      const { payload } = action;

      // Use existing ID from payload if available, otherwise generate new one
      const id = payload.id || nanoid();

      const {
        quotationNo,
        statusIndex,
        statusName,
        totalAmount,
        dueDate,
        createdDate,
        clientDetail,
        companyDetail,
      } = payload;

      const newQuotation = stampRow({
        id,
        quotationNo,
        statusIndex,
        statusName,
        totalAmount,
        dueDate,
        createdDate,
        currencyUnit: payload.currencyUnit || "₹",
        clientName: clientDetail?.name || "",
        clientDetail,
        companyDetail,
      });

      state.data.push(newQuotation);

      // Also save the full quotation details to detailList
      const newDetailEntry = stampRow({
        ...payload,
        id,  // Ensure this matches the ID used in the data array
        quotationNo,  // Ensure this matches the quotation number
      });
      
      console.log('Creating new quotation detail entry:', {
        id,
        quotationNo,
        originalPayloadId: payload.id,
        products: payload.products,
        hasProducts: !!payload.products?.length,
        hasTaxes: !!payload.taxes?.length
      });
      
      state.detailList.push(newDetailEntry);

      try {
        Promise.all([
          syncedStorage.setItem(QUOTATIONS_KEY, state.data),
          syncedStorage.setItem(QUOTATION_DETAILS, state.detailList)
        ]).catch(error => {
          console.error('Error saving quotation data to localStorage:', error);
        });
      } catch (error) {
        console.error('Error saving quotation:', error);
      }
    },

    setDeleteId: (state, action) => {
      state.deletedID = action.payload;
    },

    deleteQuotation: (state, action) => {
      const id = action.payload;
      // Soft delete with tombstone in both arrays so the deletion survives
      // a per-row merge from another device.
      state.data = state.data.map((item) => (item.id === id ? tombstoneFor(id) : item));
      state.detailList = state.detailList.map((item) => (item.id === id ? tombstoneFor(id) : item));
      state.deletedID = null; // Reset the deleted ID after deletion

      try {
        syncedStorage.setItem(QUOTATIONS_KEY, state.data);
        syncedStorage.setItem(QUOTATION_DETAILS, state.detailList);
      } catch (error) {
        console.error('Error deleting quotation:', error);
      }
    },

    setEditedId: (state, action) => {
      state.currentEditedID = action.payload;
    },

    // Settings and Modal Actions
    closeModal: (state) => {
      state.isConfirmModal = false;
      state.isConfirm = false;
    },

    openModal: (state) => {
      state.isConfirmModal = true;
    },

    setIsConfirm: (state, action) => {
      state.isConfirm = action.payload;
    },

    openSetting: (state) => {
      state.settingOpen = true;
    },

    closeSetting: (state) => {
      state.settingOpen = false;
    },

    setDefaultBg: (state, action) => {
      state.defaultBgImage = action.payload;
      try {
        syncedStorage.setItem(DEFAULT_QUOTATION_BG, action.payload);
      } catch (error) {
        console.error('Error saving default background:', error);
      }
    },

    setDefaultColor: (state, action) => {
      state.defaultColor = action.payload;
      try {
        syncedStorage.setItem(DEFAULT_QUOTATION_COLOR, action.payload);
      } catch (error) {
        console.error('Error saving default color:', error);
      }
    },

    // Form Actions
    setNewForm: (state, action) => {
      state.newForm = { ...action.payload };
      try {
        syncedStorage.setItem(QUOTATION_FORM_KEY, state.newForm);
      } catch (error) {
        console.error('Error saving quotation form:', error);
      }
    },

    updateQuotation: (state, action) => {
      const { payload } = action;
      const id = payload.id;
      
      if (!id) {
        console.error('Update quotation: No ID provided');
        return;
      }

      // Update the main data list (summary)
      const dataIndex = state.data.findIndex(item => item.id === id);
      if (dataIndex !== -1) {
        const {
          quotationNo,
          statusIndex,
          statusName,
          totalAmount,
          dueDate,
          createdDate,
          clientDetail,
        } = payload;

        state.data[dataIndex] = stampRow({
          ...state.data[dataIndex],
          quotationNo,
          statusIndex,
          statusName,
          totalAmount,
          dueDate,
          createdDate,
          clientName: clientDetail?.name || "",
        });
      }

      // Update the detail list (full data)
      const detailIndex = state.detailList.findIndex(item => item.id === id);
      if (detailIndex !== -1) {
        state.detailList[detailIndex] = stampRow({ ...payload });
      } else {
        console.warn(`Quotation detail with id ${id} not found in detailList, adding it`);
        state.detailList.push(stampRow({ ...payload }));
      }

      // Save both arrays to localStorage
      try {
        Promise.all([
          syncedStorage.setItem(QUOTATIONS_KEY, state.data),
          syncedStorage.setItem(QUOTATION_DETAILS, state.detailList)
        ]).catch(error => {
          console.error('Error updating quotation data in localStorage:', error);
        });
      } catch (error) {
        console.error('Error in quotation update localStorage save:', error);
      }
    },

    resetNewForm: (state) => {
      const newId = nanoid();
      const quotationNumber = generateInvoiceNumber(state.detailList, "Q"); // Use "Q" prefix for quotations
      
      // Get pinned template from localStorage, default to 'default'
      const pinnedTemplate = typeof window !== 'undefined' 
        ? localStorage.getItem('pinnedQuotationTemplate') || 'default'
        : 'default';
      
      state.newForm = {
        ...initialState.newForm,
        id: newId,
        quotationNo: quotationNumber,
        createdDate: new Date(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
        template: pinnedTemplate, // Set the pinned template
      };

      try {
        syncedStorage.setItem(QUOTATION_FORM_KEY, state.newForm);
      } catch (error) {
        console.error('Error resetting quotation form:', error);
      }
    },

    // Reset all modal and UI states
    resetAllStates: (state) => {
      state.deletedID = null;
      state.currentEditedID = null;
      state.isConfirmModal = false;
      state.isConfirm = false;
      state.settingOpen = false;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setAllQuotation,
  setAllQuotationDetailList,
  setNewQuotations,
  setDeleteId,
  deleteQuotation,
  setEditedId,
  closeModal,
  openModal,
  setIsConfirm,
  openSetting,
  closeSetting,
  setDefaultBg,
  setDefaultColor,
  setNewForm,
  resetNewForm,
  resetAllStates,
  updateQuotation,
} = quotationSlice.actions;

// Selectors
// See invoiceSlice.js for why these use createSelector — liveRows() returns a
// fresh .filter() array every call, so the unmemoized form trips useSelector.
export const getAllQuotationSelector = createSelector(
  [(state) => state.quotations.data],
  (data) => liveRows(data)
);
export const getAllQuotationDetailSelector = createSelector(
  [(state) => state.quotations.detailList],
  (detailList) => liveRows(detailList)
);
export const getQuotationDetailByIdSelector = (quotationId) => (state) => {
  const found = state.quotations.detailList.find((quotation) => quotation.id === quotationId);
  return isAlive(found) ? found : undefined;
};
export const getNewQuotationFormSelector = (state) => state.quotations.newForm;
export const getQuotationDeletedIdSelector = (state) => state.quotations.deletedID;
export const getQuotationEditedIdSelector = (state) => state.quotations.currentEditedID;
export const getIsQuotationConfirmModalSelector = (state) => state.quotations.isConfirmModal;
export const getIsQuotationConfirmSelector = (state) => state.quotations.isConfirm;
export const getQuotationSettingModalSelector = (state) => state.quotations.settingOpen;
export const getQuotationDefaultColorSelector = (state) => state.quotations.defaultColor;
export const getQuotationDefaultBgSelector = (state) => state.quotations.defaultBgImage;
export const getQuotationColorsSelector = (state) => state.quotations.colors;
export const getQuotationImagesSelector = (state) => state.quotations.images;

export default quotationSlice.reducer;